<template>
<div>
  <v-card>

    <v-toolbar dense flat v-bind:class="{'green lighten-3': !$vuetify.theme.dark, 'grey darken-2': $vuetify.theme.dark }">
      <v-toolbar-title>{{ $t('compAddTokenPairWatchForm.title') }}</v-toolbar-title>
      <v-spacer></v-spacer>
      <v-btn icon @click="close">
        <v-icon>mdi-window-close</v-icon>
      </v-btn>
    </v-toolbar>

    <!-- Modal Contents -->
    <v-card-text class="py-1 px-3">

      <Preloader v-show="!initializationDone" />

      <v-container v-if="initializationDone" class="adjust-center" style="max-width: 500px;">

      <v-row dense>
        <v-col class="text-center">

          <!-- token to sell -->
          <div class="mb-3">
            {{ $t('common.orderForm.label.tokenToSell') }}:
            <ERC20Token v-if="input.makerTokenAddress"
              :symbol="makerTokenInfo.symbol"
              :name="makerTokenInfo.name"
              :address="input.makerTokenAddress"
              :decimals="makerTokenInfo.decimals"
              :tokenurl="networkConfig.tokenURL" />
          </div>

          <v-menu offset-y dense>
            <template v-slot:activator="{ on }">
              <v-btn tile outlined block v-on="on">
                <span v-if="makerTokenInfo">{{ makerTokenInfo.symbol }} ({{ makerTokenInfo.name }} )</span>
                <span v-else>
                  {{ $t('common.verb.select') }}
                </span>
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense class="overflow-y-auto" max-height="300">
              <v-list-item
                v-for="(option, index) in inputOptions.makerTokenAddress"
                :key="option.value + index"
                :disabled="option.disabled"
                @click="handleMakerTokenChange(option)"
              >
                <v-list-item-title>{{ option.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <div class="input-error" v-show="inputError.makerTokenAddress">{{inputError.makerTokenAddress}}</div>

        </v-col>

        <v-col class="text-center">
          <!-- token to buy -->
          <div class="mb-3">
            {{ $t('common.orderForm.label.tokenToBuy') }}:
            <ERC20Token v-if="input.takerTokenAddress"
              :symbol="takerTokenInfo.symbol"
              :name="takerTokenInfo.name"
              :address="input.takerTokenAddress"
              :decimals="takerTokenInfo.decimals"
              :tokenurl="networkConfig.tokenURL" />
          </div>

          <v-menu offset-y dense>
            <template v-slot:activator="{ on }">
              <v-btn tile outlined block v-on="on">
                <span v-if="takerTokenInfo">{{ takerTokenInfo.symbol }} ({{ takerTokenInfo.name }} )</span>
                <span v-else>
                  {{ $t('common.verb.select') }}
                </span>
                <v-icon>mdi-chevron-down</v-icon>
              </v-btn>
            </template>
            <v-list dense class="overflow-y-auto" max-height="300">
              <v-list-item
                v-for="(option, index) in inputOptions.takerTokenAddress"
                :key="option.value + index"
                :disabled="option.disabled"
                @click="handleTakerTokenChange(option)"
              >
                <v-list-item-title>{{ option.text }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-menu>

          <div class="input-error" v-show="inputError.takerTokenAddress">{{inputError.takerTokenAddress}}</div>

        </v-col>
      </v-row>

      <!-- Add button -->
      <div class="text-center adjust-center mt-3">
        <v-btn @click="addTokenPairTracking" color="primary" :disabled="!inputComplete || inputHasError || loading">
          {{ $t('compAddTokenPairWatchForm.btnAdd') }}
          <ButtonLoading v-show="loading" class="ml-3" />
        </v-btn>
        <v-btn color="secondary" @click="close" class="ml-3">{{ $t('common.verb.close') }}</v-btn>
      </div>

    </v-container>

    </v-card-text>
  </v-card>

  <AddTokenTracking v-if="initializationDone" ref="addTokenTrackingForm" v-on:token-tracking-added-new="handleNewTokenTracking($event)" v-on:add-token-tracking-modal-hidden="handleAddTokenModalHidden" />

</div>
</template>

<script>

import { config } from '@/config'
import {mapActions, mapGetters, mapState} from 'vuex'
import Preloader from "@/components/Preloader"
import ERC20Token from "@/components/ERC20Token"
import { findTokenByAddress } from '@/utils/utils'
import AddTokenTracking from "@/components/AddTokenTracking"
import ButtonLoading from "@/components/ButtonLoading"

export default {
  name: 'AddTokenPairWatchForm',
  components: {
    Preloader,
    AddTokenTracking,
    ERC20Token,
    ButtonLoading
  },
  props: {
    ismodal: { type: Boolean, default: false },
  },
  data () {
    return {
      config: config,
      input: {
        makerTokenAddress: null,
        takerTokenAddress: null,
      },
      inputOptions: {
        makerTokenAddress: [],
        takerTokenAddress: [],
      },
      makerAddingNew: false,
      takerAddingNew: false,
      loading: false,
      initializationDone: false,
    }
  },
  computed: {
    ...mapGetters({
      network: 'getNetwork',
      account: 'getAccount',
      tokenInfo: 'getTokenInfo',
      myAddress: 'getUserAccount',
      networkConfig: 'getNetworkConfig',
    }),
    inputComplete: function () {
      if (this.input.makerTokenAddress && this.input.takerTokenAddress) {
        return true
      }
      else {
        return false
      }
    },
    inputError:{
      get: function () {
        return this.checkInputErrors()
      },
      set: function (val) {
        return val
      }
    },
    makerTokenInfo: function () {
      if (this.input.makerTokenAddress) {
        const tokenList = [ ...this.account.trackedTokens, ...this.tokenInfo.tradingPairBaseList ]
        const trackedTokenInfo = findTokenByAddress(tokenList, this.input.makerTokenAddress)
        if (trackedTokenInfo) {
          return trackedTokenInfo
        }
        else {
          return null
        }
      }
      else {
        return null
      }
    },
    takerTokenInfo: function () {
      if (this.input.takerTokenAddress) {
        const tokenList = [ ...this.account.trackedTokens, ...this.tokenInfo.tradingPairBaseList ]
        const trackedTokenInfo = findTokenByAddress(tokenList, this.input.takerTokenAddress)
        if (trackedTokenInfo) {
          return trackedTokenInfo
        }
        else {
          return null
        }
      }
      else {
        return null
      }
    },
  },
  methods: {
    ...mapActions(['loadNetwork', 'loadTokenInfo', 'loadAccount']),

    /* ------------------------------------------------------ */
    async init () {
      // if (this.networkNeedReload) {
      //   await this.loadNetwork()
      // }
      // if (!this.metaMaskLoggedIn || !this.backendLoggedIn) {
      //   this.$router.push({name: "Login"})
      //   return
      // }
      // if (this.tokenInfoNeedReload) {
      //   await this.loadTokenInfo()
      // }
      // if (this.accountNeedReload) {
      //   await this.loadAccount()
      // }

      this.input.makerTokenAddress = null
      this.input.takerTokenAddress = null

      this.getTokenSelectOptions()

      this.initializationDone = true
    },

    getTokenSelectOptions () {
      this.inputOptions.makerTokenAddress = []
      this.inputOptions.takerTokenAddress = []
      const tokenList = [ ...this.account.trackedTokens, ...this.tokenInfo.tradingPairBaseList ]
      tokenList.sort((a,b) => { return a.symbol > b.symbol ? 1 : -1 })
      const addtionalOpts = [
        { value: '--ACTION_ADD--', text: '+ ' + this.$t('common.phrase.addNewToken') }
      ]
      for (let opt of addtionalOpts) {
        this.inputOptions.makerTokenAddress.push(opt)
        this.inputOptions.takerTokenAddress.push(opt)
      }
      const seen = {}
      for (let token of tokenList) {
        const symbol = token.symbol
        const address = token.address
        if (seen[address]) {
          continue
        }
        seen[address] = true
        let optionData = {
          value: address,
          text: symbol + ' (' + token.name + ')'
        }
        this.inputOptions.makerTokenAddress.push(optionData)
        this.inputOptions.takerTokenAddress.push(optionData)
      }
      for (let opt of addtionalOpts) {
        this.inputOptions.makerTokenAddress.push(opt)
        this.inputOptions.takerTokenAddress.push(opt)
      }
    },

    checkInputErrors () {
      // console.log('checking input errors')
      this.inputHasError = false
      const result = {}
      // maker token and taker token should be differnt
      if (this.input.makerTokenAddress && this.input.takerTokenAddress) {
        if (this.input.makerTokenAddress === this.input.takerTokenAddress) {
          let msg = this.$t('compNewOrderForm.err.tokenShouldBeDifferent')
          result.makerTokenAddress = msg
          result.takerTokenAddress = msg
        }
      }
      if (Object.keys(result).length > 0) {
        this.inputHasError = true
      }
      return result
    },

    handleNewTokenTracking (newToken) {
      this.getTokenSelectOptions()
      if (this.makerAddingNew) {
        this.input.makerTokenAddress = newToken.address
      }
      if (this.takerAddingNew) {
        this.input.takerTokenAddress = newToken.address
      }
    },

    handleAddTokenModalHidden () {
      this.getTokenSelectOptions()
      if (this.input.makerTokenAddress && this.input.makerTokenAddress === '--ACTION_ADD--') {
        this.input.makerTokenAddress = null
      }
      if (this.input.takerTokenAddress && this.input.takerTokenAddress === '--ACTION_ADD--') {
        this.input.takerTokenAddress = null
      }
    },

    handleMakerTokenChange (option) {
      // console.log(option)
      if (option.value) {
        if (option.value === '--ACTION_ADD--') {
          this.input.makerTokenAddress = null
          this.makerAddingNew = true
          this.takerAddingNew = false
          this.showAddTokenTrackingForm()
        }
        else {
          this.input.makerTokenAddress = option.value
          this.makerAddingNew = false
        }
      }
    },

    handleTakerTokenChange (option) {
      if (option.value) {
        if (option.value === '--ACTION_ADD--') {
          this.input.takerTokenAddress = null
          this.takerAddingNew = true
          this.makerAddingNew = false
          this.showAddTokenTrackingForm()
        }
        else {
          this.input.takerTokenAddress = option.value
          this.takerAddingNew = false
        }
      }
    },

    close () {
      this.$emit('add-token-pair-watch-list-form-close')
    },

    async addTokenPairTracking () {
      if (!this.makerTokenInfo || !this.takerTokenInfo) {
        console.log('missing token info')
        return
      }
      this.loading = true
      const apiURL = `${this.config.backendAPIURL}/${this.network.id}/token-pair-tracking/add/${this.network.userAccount}`
      const tokenPair = {
        tokenA: {
          symbol: this.makerTokenInfo.symbol,
          address: this.makerTokenInfo.address
        },
        tokenB: {
          symbol: this.takerTokenInfo.symbol,
          address: this.takerTokenInfo.address
        },
      }
      const apiData = {
        tokenpair: tokenPair
      }
      try {
        await this.$http.post(apiURL, apiData)
        await this.loadAccount(true)
        const message = this.$t('compAddTokenPairWatchForm.successMessage', { tokenPair: tokenPair.tokenA.symbol + ' - ' + tokenPair.tokenB.symbol })
        // console.log(message)
        // this.$emit("successNotification", message)
        this.$parent.$emit("successNotification", message)
        this.$parent.$parent.$emit("successNotification", message)
      } catch (err) {
        console.log(err)
      } finally {
        this.input.makerTokenAddress = null
        this.input.takerTokenAddress = null
        this.loading = false
        this.close()
      }


    },

    showAddTokenTrackingForm () {
      this.$refs.addTokenTrackingForm.showForm()
    },

  },
  created () {
    this.init()
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped>
.add-token-pair-watch-form {
  /*font-size: 14px;*/
}

</style>
